/**
 * StockRepository - Repositorio unificado para Stock
 *
 * Implementación compartida entre web (Dexie) y mobile (SQLite).
 */

import { v4 as uuidv4 } from 'uuid';
import { IStockRepository, StockPaginatedResult, StockStatistics } from '../interfaces/IStockRepository';
import { Stock, FiltroStocks } from '../interfaces/entities';
import { BaseRepository } from './BaseRepository';
import { IDatabaseAdapter } from '../adapters/IDatabaseAdapter';
import { IProfileRepository } from '../interfaces/IProfileRepository';

/**
 * Repositorio unificado de Stock
 */
export class StockRepository extends BaseRepository<Stock, string> implements IStockRepository {

  constructor(db: IDatabaseAdapter, profileRepo?: IProfileRepository) {
    super(db, 'stocks', null); // stocks es una tabla local, solo stocks_historico se sincroniza

    if (profileRepo) {
      this.setProfileRepository(profileRepo);
    }
  }

  async add(data: Omit<Stock, 'id'>): Promise<string> {
    const id = uuidv4();
    const now = this.getCurrentTimestamp();
    const profile = await this.getCurrentProfile();

    const record: Stock = {
      ...data,
      id,
      updated_at: now,
      empresa_id: data.empresa_id || profile?.empresa_activa_id || ''
    };

    await this.db.insert('stocks', record);
    this.emitSync('INSERT', record);

    return id;
  }

  async update(data: Partial<Stock> & { id: string }): Promise<void> {
    const updatedData = {
      ...data,
      updated_at: this.getCurrentTimestamp()
    };
    await this.db.update('stocks', data.id, updatedData);
    this.emitSync('UPDATE', updatedData as any);
  }

  /**
   * Upsert real: crea la fila si el ID no existe, reemplaza si existe.
   *
   * update() no sirve para reconstruir el inventario en un caché fresco:
   * Dexie Table.update() sobre una key inexistente es NO-OP, así que la
   * reconstrucción post-sync escribía 0 filas en navegadores limpios.
   */
  async put(data: Stock): Promise<void> {
    const record: Stock = {
      ...data,
      updated_at: this.getCurrentTimestamp()
    };
    await this.db.put('stocks', record);
    this.emitSync('UPDATE', record as any);
  }

  async delete(id: string): Promise<void> {
    await this.db.delete('stocks', id);
    this.emitSync('DELETE', id);
  }

  async getById(id: string): Promise<Stock | undefined> {
    return this.db.get<Stock>('stocks', id);
  }

  async getByFiltros(filtros: FiltroStocks): Promise<Stock[]> {
    // Partimos de los registros de la empresa activa para no mezclar datos
    // de otras empresas.
    let filtered = await this.getAllForActiveEmpresa();

    if (filtros.categoria) {
      filtered = filtered.filter(s => s.categoria === filtros.categoria);
    }

    // Filtros que requieren lógica adicional
    if (filtros.nombre) {
      const lowerNombre = filtros.nombre.toLowerCase();
      filtered = filtered.filter(s => s.nombre?.toLowerCase().includes(lowerNombre));
    }
    if (filtros.stock_bajo) {
      filtered = filtered.filter(s => (s.cantidad || 0) <= 10);
    }

    return filtered;
  }

  async searchByNombre(nombre: string): Promise<Stock[]> {
    const lower = nombre.toLowerCase();
    const all = await this.getAllForActiveEmpresa();
    return all.filter(s => (s.nombre ?? '').toLowerCase().includes(lower));
  }

  async getByCategoria(categoria: string): Promise<Stock[]> {
    const all = await this.getAllForActiveEmpresa();
    return all.filter(s => s.categoria === categoria);
  }

  async getByEmpresa(empresaId: string): Promise<Stock[]> {
    return this.db.query<Stock>('stocks', {
      field: 'empresa_id',
      operator: 'equals',
      value: empresaId
    });
  }

  async getStockBajo(umbral: number): Promise<Stock[]> {
    const all = await this.getAllForActiveEmpresa();
    return all.filter(s => (s.cantidad || 0) <= umbral);
  }

  async getAllPaginated(
    offset: number,
    limit: number,
    filtros?: FiltroStocks,
    sortBy: string = 'nombre',
    sortDirection: 'asc' | 'desc' = 'asc'
  ): Promise<StockPaginatedResult> {
    let stocks = filtros ? await this.getByFiltros(filtros) : await this.getAllForActiveEmpresa();

    // Sort
    stocks.sort((a, b) => {
      const aVal = (a as any)[sortBy] || '';
      const bVal = (b as any)[sortBy] || '';
      const cmp = String(aVal).localeCompare(String(bVal));
      return sortDirection === 'asc' ? cmp : -cmp;
    });

    const total = stocks.length;
    const items = stocks.slice(offset, offset + limit);

    return {
      items,
      total,
      hasMore: offset + limit < total
    };
  }

  async getStatistics(): Promise<StockStatistics> {
    const stocks = await this.getAllForActiveEmpresa();

    const totalProductos = stocks.length;
    const valorTotal = stocks.reduce((sum, s) => sum + (s.cantidad || 0), 0);
    const productosStockBajo = stocks.filter(s => (s.cantidad || 0) <= 10).length;

    const porCategoria = new Map<string, { cantidad: number; valor: number }>();
    stocks.forEach(s => {
      const cat = s.categoria || 'Sin categoría';
      const existing = porCategoria.get(cat) || { cantidad: 0, valor: 0 };
      porCategoria.set(cat, {
        cantidad: existing.cantidad + 1,
        valor: existing.valor + (s.cantidad || 0)
      });
    });

    return {
      totalProductos,
      valorTotal,
      productosStockBajo,
      porCategoria
    };
  }

  async updateCantidad(id: string, cantidad: number): Promise<void> {
    await this.db.update('stocks', id, {
      cantidad,
      updated_at: this.getCurrentTimestamp()
    } as any);
    this.emitSync('UPDATE', { id, cantidad, updated_at: this.getCurrentTimestamp() } as any);
  }

  async getDisponibles(): Promise<Stock[]> {
    const all = await this.getAllForActiveEmpresa();
    return all.filter(s => (s.cantidad || 0) > 0);
  }

  async findByNombreAndCategoria(nombre: string, categoria: string, empresaId?: string): Promise<Stock | undefined> {
    const filters: Array<{ field: string; operator: string; value: unknown }> = [
      { field: 'nombre', operator: 'equals', value: nombre },
      { field: 'categoria', operator: 'equals', value: categoria }
    ];
    // El caché local es multiempresa: sin este filtro, una compra en la
    // empresa B puede sumar cantidad al stock homónimo de la empresa A.
    if (empresaId) {
      filters.push({ field: 'empresa_id', operator: 'equals', value: empresaId });
    }
    const results = await this.db.queryMultiple<Stock>('stocks', filters as any);
    return results[0];
  }

  // ==================== Bulk Operations ====================

  async bulkAdd(items: Stock[]): Promise<void> {
    await this.db.bulkInsert('stocks', items);
    this.emitBulkSync('BULK_INSERT', items);
  }

  async bulkAddSilent(items: Stock[]): Promise<void> {
    await this.db.bulkInsert('stocks', items);
    items.forEach(item => this.emitLocalSync('INSERT', item));
  }

  async bulkPut(items: Stock[]): Promise<void> {
    await this.db.bulkUpsert('stocks', items);
    this.emitBulkSync('BULK_INSERT', items);
  }

  async bulkPutSilent(items: Stock[]): Promise<void> {
    await this.db.bulkUpsert('stocks', items);
    items.forEach(item => this.emitLocalSync('UPDATE', item));
  }

  async bulkDelete(ids: string[]): Promise<void> {
    await this.db.bulkDelete('stocks', ids);
    this.emitBulkSync('BULK_DELETE', ids);
  }

  async bulkDeleteSilent(ids: string[]): Promise<void> {
    await this.db.bulkDelete('stocks', ids);
    ids.forEach(id => this.emitLocalSync('DELETE', id));
  }

  async getLastByDate(dateColumn: string = 'updated_at'): Promise<Stock | undefined> {
    const results = await this.db.query<Stock>('stocks', undefined, {
      column: dateColumn,
      direction: 'desc'
    });
    return results[0] || undefined;
  }
}
